import {
  OverheadExpense,
  formatOrdinalDay,
  resolveOverheadRecurringDay,
  resolveOverheadRenewalMonth,
} from '../types/finances';

export type OverheadDatePreset =
  | 'this-month'
  | 'last-month'
  | 'this-quarter'
  | 'last-quarter'
  | 'ytd'
  | 'last-90'
  | 'next-90'
  | 'this-year'
  | 'last-year'
  | 'custom'
  | 'all';

export type OverheadDayBucket = 'any' | '1-10' | '11-20' | '21-31' | 'exact';

export type OverheadDateRange = {
  start: Date | null;
  end: Date | null;
};

export const OVERHEAD_DATE_PRESETS: { id: OverheadDatePreset; label: string }[] = [
  { id: 'this-month', label: 'This month' },
  { id: 'last-month', label: 'Last month' },
  { id: 'this-quarter', label: 'This quarter' },
  { id: 'last-quarter', label: 'Last quarter' },
  { id: 'ytd', label: 'Year to date' },
  { id: 'last-90', label: 'Last 90 days' },
  { id: 'next-90', label: 'Next 90 days' },
  { id: 'this-year', label: 'This year' },
  { id: 'last-year', label: 'Last year' },
  { id: 'custom', label: 'Custom' },
  { id: 'all', label: 'All dates' },
];

export const OVERHEAD_DAY_BUCKETS: { id: OverheadDayBucket; label: string }[] = [
  { id: 'any', label: 'Any day' },
  { id: '1-10', label: '1st–10th' },
  { id: '11-20', label: '11th–20th' },
  { id: '21-31', label: '21st–31st' },
  { id: 'exact', label: 'Exact day' },
];

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MAX_OCCURRENCES = 400;

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function endOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function endOfMonth(d: Date): Date {
  return endOfDay(new Date(d.getFullYear(), d.getMonth() + 1, 0));
}

export function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

export function toInputDate(d: Date | null | undefined): string {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function fromInputDate(s: string): Date | null {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function quarterStart(d: Date): Date {
  return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
}

export function resolveOverheadDateRange(
  preset: OverheadDatePreset,
  customStart?: Date | null,
  customEnd?: Date | null,
  now: Date = new Date(),
): OverheadDateRange {
  const today = startOfDay(now);
  if (preset === 'all') return { start: null, end: null };
  if (preset === 'custom') {
    const start = customStart ? startOfDay(customStart) : null;
    const end = customEnd ? endOfDay(customEnd) : null;
    return { start, end };
  }
  if (preset === 'this-month') return { start: startOfMonth(today), end: endOfMonth(today) };
  if (preset === 'last-month') {
    const prev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    return { start: startOfMonth(prev), end: endOfMonth(prev) };
  }
  if (preset === 'this-quarter') {
    const start = quarterStart(today);
    const end = endOfMonth(new Date(start.getFullYear(), start.getMonth() + 2, 1));
    return { start, end };
  }
  if (preset === 'last-quarter') {
    const thisQ = quarterStart(today);
    const start = new Date(thisQ.getFullYear(), thisQ.getMonth() - 3, 1);
    const end = endOfMonth(new Date(start.getFullYear(), start.getMonth() + 2, 1));
    return { start, end };
  }
  if (preset === 'ytd') return { start: new Date(today.getFullYear(), 0, 1), end: endOfDay(today) };
  if (preset === 'last-90') return { start: addDays(today, -89), end: endOfDay(today) };
  if (preset === 'next-90') return { start: today, end: endOfDay(addDays(today, 89)) };
  if (preset === 'this-year') {
    return { start: new Date(today.getFullYear(), 0, 1), end: endOfMonth(new Date(today.getFullYear(), 11, 1)) };
  }
  const lastYear = today.getFullYear() - 1;
  return { start: new Date(lastYear, 0, 1), end: endOfMonth(new Date(lastYear, 11, 1)) };
}

export function formatOverheadRangeLabel(range: OverheadDateRange): string {
  if (!range.start && !range.end) return 'All dates';
  const fmt = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (range.start && range.end) return `${fmt(range.start)} – ${fmt(range.end)}`;
  if (range.start) return `From ${fmt(range.start)}`;
  return `Through ${fmt(range.end!)}`;
}

function lastDayOfMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

export function billDateInMonth(year: number, month: number, day: number): Date {
  return new Date(year, month, Math.min(Math.max(1, day), lastDayOfMonth(year, month)));
}

function inRange(d: Date, start: Date | null, end: Date | null): boolean {
  const t = startOfDay(d).getTime();
  if (start && t < startOfDay(start).getTime()) return false;
  if (end && t > startOfDay(end).getTime()) return false;
  return true;
}

function firstOnOrAfter(anchor: Date, stepDays: number, windowStart: Date): Date {
  const a = startOfDay(anchor);
  const w = startOfDay(windowStart);
  if (a.getTime() >= w.getTime()) return a;
  const diff = Math.round((w.getTime() - a.getTime()) / MS_PER_DAY);
  const steps = Math.ceil(diff / stepDays);
  return addDays(a, steps * stepDays);
}

export function dayMatchesBucket(day: number, bucket: OverheadDayBucket, exactDay?: number | null): boolean {
  if (bucket === 'any') return true;
  if (bucket === '1-10') return day >= 1 && day <= 10;
  if (bucket === '11-20') return day >= 11 && day <= 20;
  if (bucket === '21-31') return day >= 21 && day <= 31;
  const exact = exactDay != null ? Math.round(exactDay) : null;
  if (exact == null || exact < 1 || exact > 31) return true;
  return day === exact;
}

/**
 * Dated charges for one overhead row inside a window. Honors start/end dates.
 * Deactivated rows without an end date stop at `now`.
 */
export function overheadOccurrencesInRange(
  expense: OverheadExpense,
  rangeStart: Date | null,
  rangeEnd: Date | null,
  now: Date = new Date(),
): Date[] {
  const start = expense.startDate ? startOfDay(expense.startDate) : null;
  if (!start) return [];

  const explicitEnd = expense.endDate ? startOfDay(expense.endDate) : null;
  const inactiveCap = !expense.isActive && !explicitEnd ? startOfDay(now) : null;
  const windowStart = rangeStart
    ? startOfDay(rangeStart.getTime() > start.getTime() ? rangeStart : start)
    : start;
  let windowEnd = rangeEnd ? startOfDay(rangeEnd) : null;
  if (explicitEnd) {
    windowEnd = windowEnd && windowEnd.getTime() < explicitEnd.getTime() ? windowEnd : explicitEnd;
  }
  if (inactiveCap) {
    windowEnd = windowEnd && windowEnd.getTime() < inactiveCap.getTime() ? windowEnd : inactiveCap;
  }
  if (windowEnd && windowStart.getTime() > windowEnd.getTime()) return [];

  const day = resolveOverheadRecurringDay(expense);
  const out: Date[] = [];
  const push = (d: Date) => {
    if (out.length >= MAX_OCCURRENCES) return;
    if (inRange(d, windowStart, windowEnd)) out.push(startOfDay(d));
  };

  if (expense.frequency === 'one-time') {
    push(start);
    return out;
  }

  if (expense.frequency === 'daily') {
    let cursor = windowStart;
    const last = windowEnd ?? addDays(windowStart, MAX_OCCURRENCES - 1);
    while (cursor.getTime() <= last.getTime() && out.length < MAX_OCCURRENCES) {
      push(cursor);
      cursor = addDays(cursor, 1);
    }
    return out;
  }

  if (expense.frequency === 'weekly' || expense.frequency === 'biweekly') {
    const step = expense.frequency === 'weekly' ? 7 : 14;
    let cursor = firstOnOrAfter(start, step, windowStart);
    const last = windowEnd ?? addDays(windowStart, step * MAX_OCCURRENCES);
    while (cursor.getTime() <= last.getTime() && out.length < MAX_OCCURRENCES) {
      push(cursor);
      cursor = addDays(cursor, step);
    }
    return out;
  }

  if (expense.frequency === 'monthly') {
    let y = windowStart.getFullYear();
    let m = windowStart.getMonth() - 1;
    const last = windowEnd ?? new Date(windowStart.getFullYear() + 40, windowStart.getMonth(), 1);
    while (out.length < MAX_OCCURRENCES) {
      m += 1;
      if (m > 11) {
        m = 0;
        y += 1;
      }
      const billed = billDateInMonth(y, m, day);
      if (billed.getTime() > last.getTime()) break;
      push(billed);
    }
    return out;
  }

  if (expense.frequency === 'quarterly') {
    let y = start.getFullYear();
    let m = start.getMonth();
    const last = windowEnd ?? new Date(windowStart.getFullYear() + 40, windowStart.getMonth(), 1);
    while (out.length < MAX_OCCURRENCES) {
      const billed = billDateInMonth(y, m, day);
      if (billed.getTime() > last.getTime()) break;
      push(billed);
      m += 3;
      while (m > 11) {
        m -= 12;
        y += 1;
      }
    }
    return out;
  }

  // annually — renewal month is explicit, else the start-date month
  const renewalMonthIndex = resolveOverheadRenewalMonth(expense) - 1;
  const lastYear = (windowEnd ?? new Date(windowStart.getFullYear() + 40, 0, 1)).getFullYear();
  for (let y = windowStart.getFullYear() - 1; y <= lastYear && out.length < MAX_OCCURRENCES; y++) {
    push(billDateInMonth(y, renewalMonthIndex, day));
  }
  return out;
}

export function occurrenceAmount(expense: OverheadExpense, activeHeadcount: number): number {
  const amount = Number(expense.amount) || 0;
  if (expense.scalesWith === 'per_active_employee') return amount * activeHeadcount;
  return amount;
}

export type OverheadOccurrence = {
  expense: OverheadExpense;
  date: Date;
  amount: number;
};

export function collectOverheadOccurrences(
  expenses: OverheadExpense[],
  range: OverheadDateRange,
  activeHeadcount: number,
  now?: Date,
): OverheadOccurrence[] {
  const rows: OverheadOccurrence[] = [];
  for (const expense of expenses) {
    const dates = overheadOccurrencesInRange(expense, range.start, range.end, now);
    const amount = occurrenceAmount(expense, activeHeadcount);
    for (const date of dates) rows.push({ expense, date, amount });
  }
  rows.sort((a, b) => b.date.getTime() - a.date.getTime() || a.expense.name.localeCompare(b.expense.name));
  return rows;
}

export function filterOverheadByDate(
  expenses: OverheadExpense[],
  range: OverheadDateRange,
  dayBucket: OverheadDayBucket,
  exactDay: number | null,
  now?: Date,
): OverheadExpense[] {
  const unbounded = !range.start && !range.end;
  return expenses.filter((expense) => {
    const recurringDay = resolveOverheadRecurringDay(expense);
    if (!dayMatchesBucket(recurringDay, dayBucket, exactDay)) return false;
    if (unbounded) return true;
    return overheadOccurrencesInRange(expense, range.start, range.end, now).length > 0;
  });
}

export function formatOccurrenceSummary(dates: Date[]): string {
  if (dates.length === 0) return '—';
  if (dates.length === 1) {
    return dates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  const first = dates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${dates.length}× from ${first}`;
}

export function formatOccurrenceDay(date: Date): string {
  return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · ${formatOrdinalDay(date.getDate())}`;
}

/** When Transactions view uses All dates, bound expansion to this calendar year. */
export function transactionRangeForPreset(
  preset: OverheadDatePreset,
  range: OverheadDateRange,
  now: Date = new Date(),
): OverheadDateRange {
  if (preset !== 'all' && (range.start || range.end)) return range;
  return resolveOverheadDateRange('this-year', null, null, now);
}
